/**
 * 音频播放 Mixin
 * 提供音频播放的辅助方法和计算属性
 * 注意：事件监听已移到 App.vue 中初始化，此 mixin 只提供方法和状态访问
 */

import { mapState, mapGetters, mapActions } from "vuex";
import audioManager from "@/utils/audioManager";
export default {
  computed: {
    ...mapState("audio", [
      "currentTrack",
      "isPlaying",
      "isLoading",
      "currentTime",
      "duration",
      "playlist",
      "currentIndex",
      "playlistInfo"
    ]),

    ...mapGetters("audio", [
      "canPlayNext",
      "canPlayPrevious",
      "playProgress",
      "formattedCurrentTime",
      "formattedDuration",
      // 是否展示播放列表弹窗
      "isShowPlayDialog"
    ])
  },

  methods: {
    ...mapActions("audio", [
      "playTrack",
      "togglePlay",
      "playNext",
      "playPrevious",
      "setPlaylist",
      "clearPlaylist",
      "playFromPlaylist",
      "setPlaybackRate",
      "setVolume",
      "seekTo",
      "setSleepTimer",
      "cancelSleepTimer",
      "setShowPlayDialog"
    ]),
    ...mapActions(["openPayDialog"]),

    /**
     * 播放指定音轨
     * @param {Object} track - 音轨对象
     * @param {Number} index - 播放列表索引（可选，如果不传则自动从播放列表中查找）
     */
    async handlePlayTrack(track, index) {
      // 如果没有传入 index，则根据 track 的 id/customId 从播放列表中查找
      if (index === undefined || index === null) {
        const trackId = track.id || track.customId;
        index = this.playlist.findIndex(item => {
          const itemId = item.id || item.customId;
          return itemId === trackId;
        });

        if (index === -1) {
          console.error("[AudioMixin] 在播放列表中找不到指定音轨:", track);
          throw new Error("在播放列表中找不到指定音轨");
        }

        console.log(`[AudioMixin] 自动找到音轨索引: ${index}`);
      }

      console.log(
        "[AudioMixin] 播放指定音轨:",
        track.title || track.name,
        "索引:",
        index
      );
      await this.playTrack({ track, index });
    },

    /**
     * 切换播放/暂停
     */
    handleTogglePlay() {
      console.log("[AudioMixin] 切换播放/暂停");
      this.togglePlay();
    },

    /**
     * 播放上一首
     */
    handlePlayPrevious() {
      if (!this.canPlayPrevious) {
        console.log("[AudioMixin] 已是第一首，无法播放上一首");
        return;
      }
      console.log("[AudioMixin] 播放上一首");
      this.playPrevious();
    },

    /**
     * 播放下一首
     */
    handlePlayNext() {
      if (!this.canPlayNext) {
        console.log("[AudioMixin] 已是最后一首，无法播放下一首");
        return;
      }

      let curIdx = this.currentTrack.idx;
      if (this.playlist[curIdx]) {
        //看下一首的isUnLock状态
        let nextIsUnLock = this.playlist[curIdx].isUnLock;
        console.log("nextIsUnLock: ", nextIsUnLock);

        if (!nextIsUnLock) {
          // console.log("this.playlist[curIdx]:", this.playlist[curIdx].customId);
          let customId = this.playlist[curIdx].customId;

          let parts = customId.split("-");
          let payType = parseInt(parts[0]);
          let lessionOrAudioBookId = parseInt(parts[1]);

          this.openPayDialog({
            payType: parseInt(payType),
            lessionOrAudioBookId: parseInt(lessionOrAudioBookId)
          });

          return;
        }
      }

      console.log("[AudioMixin] 播放下一首");
      this.playNext();
    },

    /**
     * 跳转到指定进度
     * @param {Number} time - 时间（秒）
     */
    handleSeekTo(time) {
      console.log("[AudioMixin] 跳转到:", time);
      audioManager.seek(time);
    },

    /**
     * 设置播放列表并播放
     * @param {Array} playlist - 播放列表
     * @param {Object} info - 播放列表信息
     * @param {Number} startIndex - 起始播放索引，默认0
     */
    async handleSetPlaylistAndPlay(playlist, info, startIndex = 0) {
      console.log("[AudioMixin] 设置播放列表并播放");

      if (!playlist || playlist.length === 0) {
        console.warn("[AudioMixin] 播放列表为空");
        return;
      }

      // 设置播放列表
      await this.setPlaylist({ playlist, info });

      // 播放指定索引的音轨
      const track = playlist[startIndex];
      if (track) {
        await this.handlePlayTrack(track, startIndex);
      }
    },

    /**
     * 设置播放速率
     * @param {Number} rate - 播放速率 0.5-2.0
     */
    handleSetPlaybackRate(rate) {
      console.log("[AudioMixin] 设置播放速率:", rate);
      this.setPlaybackRate(rate);
    },

    /**
     * 设置音量
     * @param {Number} volume - 音量 0-1
     */
    handleSetVolume(volume) {
      console.log("[AudioMixin] 设置音量:", volume);
      this.setVolume(volume);
    },

    /**
     * 清空播放列表
     */
    handleClearPlaylist() {
      console.log("[AudioMixin] 清空播放列表");
      this.clearPlaylist();
    }
  }
};
